import CCColors from '@/Utils/CCColors';
import {STARTING_FEN} from '@/Utils/CommonUtils';
import type {CevalPvLine} from '@/Components/CevalPanel';
import {PracticeGoal} from '../types';

export const sanitizeFen = (fenString: string | undefined | null) => {
  if (!fenString || fenString === 'start') {
    return STARTING_FEN;
  }
  return fenString;
};

// Winning-chances curve used to fade multi-line engine arrows: maps a
// white-POV centipawn score onto [-1, 1] so an eval gap translates into an
// opacity gap the same way for +1 vs +2 as for 0 vs +1.
export const winningChances = (pv: CevalPvLine): number => {
  if (typeof pv.mate === 'number') {
    return pv.mate > 0 ? 1 : -1;
  }
  const cp = Math.max(-1000, Math.min(1000, pv.cp ?? 0));
  return 2 / (1 + Math.exp(-0.004 * cp)) - 1;
};

export const isDatabaseId = (id: any): boolean => {
  if (typeof id === 'number') {
    return true;
  }
  if (typeof id === 'string') {
    return /^\d+$/.test(id);
  }
  return false;
};

// White-POV centipawns from a StockfishComponent onResult payload (mate
// scores are collapsed to a large signed magnitude so they still compare
// correctly against ordinary cp scores).
export const whitePovCpFromResult = (result: any): number => {
  if (result.isMate) {
    const raw = Number(result.rawScore);
    // `score mate 0` means the side to move is ALREADY mated — the only mate
    // score with no sign to read. Treating it as +1 (which `mateVal || 1` did)
    // handed the win to whichever side had just been checkmated. Resolve it
    // from whose turn it is instead: mated side to move = the other side won.
    let mateVal = Number.isFinite(raw) ? raw : 0;
    if (mateVal === 0) {
      return result.baseTurn === 'b' ? 100000 : -100000;
    }
    if (result.baseTurn === 'b') {
      mateVal = -mateVal;
    }
    return Math.sign(mateVal) * 100000;
  }
  return Number(result.score) * 100;
};

// Same categorization WorkbookMovesList uses for coloring a move comment
// (see its own commentAccentColor) - duplicated here rather than shared
// since it's a 4-line pure function and the two components don't
// otherwise share utilities.
export const commentAccentColor = (text: string): string => {
  if (text.startsWith('Blunder.')) {
    return CCColors.Red;
  }
  if (text.startsWith('Mistake.')) {
    return CCColors.Progress.Bar;
  }
  if (text.startsWith('Inaccuracy.')) {
    return CCColors.Blue;
  }
  return CCColors.Workbook.Brown;
};

export const practiceGoalLabel = (goal: PracticeGoal | null | undefined) => {
  if (!goal?.type) {
    return null;
  }
  switch (goal.type) {
    case 'mate':
      return 'Goal: Checkmate the opponent';
    case 'mateIn':
      return goal.value
        ? `Goal: Checkmate the opponent in ${goal.value} move${
            goal.value > 1 ? 's' : ''
          }`
        : 'Goal: Find a forced checkmate';
    case 'equalize':
      return 'Goal: Equalize the position';
    case 'advantage':
      return 'Goal: Reach a winning advantage';
    case 'promotion':
      return 'Goal: Promote a pawn';
    default:
      return null;
  }
};
